import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  X,
  CheckCheck,
  Trash2,
  Bell,
  MessageSquare,
  Send,
  FileText,
  Loader2,
  AlertTriangle,
  AlertCircle,
  Info,
  Zap,
  Archive,
  Clock,
  MoreVertical,
  Check,
  BellOff,
} from "lucide-react";
import { Notification } from "../types";
import { useTranslation } from "../i18n/hooks/useTranslation";
import { useAuth } from "../contexts/AuthContext";
import { useNotifications } from "../contexts/NotificationContext";
import { formatDate, formatDateTime } from "../utils/dateUtils";
import { getQuoteStatusColor, getNotificationCardBg, getNotificationCardBorder, getNotificationCardHeading, getNotificationCardText } from "../utils/statusColors";

interface NotificationsPaneProps {
  onClose: () => void;
}

interface LineItem {
  description: string;
  quantity: number;
  unitPrice: number;
  taxRate: number;
  taxAmount: number;
  lineTotal: number;
  isOptional?: boolean;
}

interface Quote {
  _id: string;
  quoteNumber: string;
  title: string;
  clientId: {
    _id: string;
    name: string;
    email?: string;
    phone?: string;
  };
  projectId?: {
    _id: string;
    name: string;
  };
  templateId?: {
    _id: string;
    name: string;
  };
  status: "CREATED" | "SENT" | "APPROVED" | "DECLINED";
  headerText?: string;
  footerText?: string;
  termsAndConditions?: string;
  lineItems: LineItem[];
  subtotal: number;
  totalTax: number;
  total: number;
  currencyCode: string;
  validUntil?: string;
  issuedDate: string;
  sentDate?: string;
  sentVia?: ("EMAIL" | "SMS")[];
  publicToken?: string;
  createdBy?: {
    firstName: string;
    lastName: string;
  };
  createdAt: string;
}

interface QuoteMessage {
  _id: string;
  clientName: string;
  clientEmail?: string;
  message: string;
  isFromClient: boolean;
  staffUserId?: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  read: boolean;
  createdAt: string;
  formattedDate?: string; // Pre-formatted date string
  staffName?: string | null; // Pre-computed staff name
}

export const NotificationsPane: React.FC<NotificationsPaneProps> = ({
  onClose,
}) => {
  const { isRTL } = useTranslation();
  const { user } = useAuth();
  const {
    notifications,
    unreadCount: contextUnreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    deleteNotification: contextDeleteNotification,
    archiveNotification,
    snoozeNotification,
  } = useNotifications();
  const [selectedQuoteId, setSelectedQuoteId] = useState<string | null>(null);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [showMessagesModal, setShowMessagesModal] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState<Quote | null>(null);
  const [quoteMessages, setQuoteMessages] = useState<QuoteMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [replyMessage, setReplyMessage] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [loadingNotificationId, setLoadingNotificationId] = useState<
    string | null
  >(null);
  const [loadingQuickReplyId, setLoadingQuickReplyId] = useState<string | null>(
    null,
  );
  const [showSnoozeMenu, setShowSnoozeMenu] = useState<string | null>(null);
  const [showActionsMenu, setShowActionsMenu] = useState<string | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
  const notificationRefs = useRef<(HTMLDivElement | null)[]>([]);

  const fetchQuoteById = async (quoteId: string) => {
    try {
      setLoadingQuote(true);
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("No token found");
        return;
      }

      console.log("Fetching quote with ID:", quoteId);
      const response = await fetch(`${apiUrl}/api/quotes/${quoteId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      console.log("Quote fetch response:", data);
      if (data.success) {
        setSelectedQuote(data.data);
        setShowQuoteModal(true);
      } else {
        console.error("Failed to fetch quote:", data.message);
      }
    } catch (error) {
      console.error("Failed to fetch quote:", error);
    } finally {
      setLoadingQuote(false);
    }
  };

  const fetchQuoteMessages = async (quoteId: string) => {
    try {
      setLoadingMessages(true);
      const token = localStorage.getItem("token");
      if (!token) return;

      const response = await fetch(`${apiUrl}/api/quotes/${quoteId}/messages`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        // Pre-format dates to avoid expensive date formatting on every render
        const formattedMessages = (data.data || []).map(
          (msg: QuoteMessage) => ({
            ...msg,
            formattedDate: formatDateTime(msg.createdAt),
            staffName: msg.isFromClient
              ? null
              : `${msg.staffUserId?.firstName || ""} ${msg.staffUserId?.lastName || ""}`.trim() ||
                "You",
          }),
        );
        setQuoteMessages(formattedMessages);
      }
    } catch (error) {
      console.error("Failed to fetch quote messages:", error);
    } finally {
      setLoadingMessages(false);
    }
  };

  const sendQuickReply = async (quoteId: string, message: string) => {
    try {
      setSendingReply(true);
      const token = localStorage.getItem("token");
      if (!token) return;

      const response = await fetch(`${apiUrl}/api/quotes/${quoteId}/messages`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ message: message.trim() }),
      });

      const data = await response.json();
      if (data.success) {
        // Format the new message with pre-computed values
        const newMessage = {
          ...data.data,
          formattedDate: formatDateTime(data.data.createdAt),
          staffName: data.data.isFromClient
            ? null
            : `${data.data.staffUserId?.firstName || ""} ${data.data.staffUserId?.lastName || ""}`.trim() ||
              "You",
        };
        setQuoteMessages((prev) => [...prev, newMessage]);
        setReplyMessage("");
      }
    } catch (error) {
      console.error("Failed to send reply:", error);
    } finally {
      setSendingReply(false);
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    // Prevent multiple clicks
    if (loadingNotificationId || loadingQuote) return;

    console.log("Notification clicked:", notification);
    if (notification.quoteId) {
      setLoadingNotificationId(notification._id);
      try {
        console.log("Fetching quote:", notification.quoteId);
        await fetchQuoteById(notification.quoteId);
        // Mark as read when clicked
        if (!notification.read) {
          markAsRead(notification._id);
        }
      } finally {
        setLoadingNotificationId(null);
      }
    } else {
      console.log("No quoteId found in notification");
    }
  };

  const fetchQuoteMinimal = async (quoteId: string) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return null;

      const response = await fetch(`${apiUrl}/api/quotes/${quoteId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success && data.data) {
        // Return only minimal quote info needed for messages modal
        return {
          _id: data.data._id,
          quoteNumber: data.data.quoteNumber,
          title: data.data.title,
        };
      }
      return null;
    } catch (error) {
      console.error("Failed to fetch minimal quote info:", error);
      return null;
    }
  };

  const handleQuickReply = async (notification: Notification) => {
    // Prevent multiple clicks
    if (loadingQuickReplyId || loadingQuote || loadingMessages) return;

    console.log("Reply clicked for notification:", notification);
    if (notification.quoteId) {
      setLoadingQuickReplyId(notification._id);
      try {
        console.log("Opening messages modal for quote:", notification.quoteId);
        setSelectedQuoteId(notification.quoteId);
        // Fetch only minimal quote info (quoteNumber and title) for the messages modal header
        const minimalQuote = await fetchQuoteMinimal(notification.quoteId);
        if (minimalQuote) {
          setSelectedQuote(minimalQuote as Quote);
        }
        await fetchQuoteMessages(notification.quoteId);
        setShowMessagesModal(true);
        // Mark as read when reply is opened
        if (!notification.read) {
          markAsRead(notification._id);
        }
      } finally {
        setLoadingQuickReplyId(null);
      }
    } else {
      console.error("No quoteId found in notification for Reply");
    }
  };

  const handleSendReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedQuoteId || !replyMessage.trim()) return;
    await sendQuickReply(selectedQuoteId, replyMessage);
  };

  const getNotificationColor = (type: Notification["type"]) => {
    const notificationType = type || "info";
    return `${getNotificationCardBg(notificationType)} ${getNotificationCardBorder(notificationType)} border`;
  };

  const getNotificationIcon = (notification: Notification) => {
    const { type, priority } = notification;
    
    // Critical priority always gets Zap icon
    if (priority === "critical") {
      return <Zap className="w-4 h-4 text-error-600 dark:text-error-400" />;
    }
    
    // Otherwise, use type-based icons
    switch (type) {
      case "success":
        return <CheckCheck className="w-4 h-4 text-success-600 dark:text-success-400" />;
      case "error":
        return <AlertCircle className="w-4 h-4 text-error-600 dark:text-error-400" />;
      case "warning":
        return <AlertTriangle className="w-4 h-4 text-warning-600 dark:text-warning-400" />;
      case "info":
      default:
        // For info type, use priority to determine the icon
        if (priority === "high") {
          return <Bell className="w-4 h-4 text-accent-600 dark:text-accent-400" />;
        }
        return <Info className="w-4 h-4 text-accent-600 dark:text-accent-400" />;
    }
  };

  const getIconBgColor = (notification: Notification) => {
    const { type, priority, read } = notification;
    
    if (read) {
      return "bg-gray-100 dark:bg-gray-700";
    }
    
    // Critical priority gets error color
    if (priority === "critical") {
      return "bg-error-100 dark:bg-error-900/30";
    }
    
    // Type-based colors
    switch (type) {
      case "success":
        return "bg-success-100 dark:bg-success-900/30";
      case "error":
        return "bg-error-100 dark:bg-error-900/30";
      case "warning":
        return "bg-warning-100 dark:bg-warning-900/30";
      case "info":
      default:
        return "bg-accent-100 dark:bg-accent-900/30";
    }
  };

  const getPriorityBorderColor = (priority: Notification["priority"]) => {
    switch (priority) {
      case "critical":
        return "border-l-error-600 dark:border-l-error-400";
      case "high":
        return "border-l-warning-600 dark:border-l-warning-400";
      case "normal":
        return "border-l-accent-600 dark:border-l-accent-400";
      case "low":
        return "border-l-gray-400 dark:border-l-gray-500";
      default:
        return "border-l-accent-600 dark:border-l-accent-400";
    }
  };

  const getPriorityLabel = (priority: Notification["priority"]) => {
    switch (priority) {
      case "critical":
        return "CRITICAL";
      case "high":
        return "HIGH";
      case "normal":
        return null; // Don't show label for normal priority
      case "low":
        return null; // Don't show label for low priority
      default:
        return null;
    }
  };

  const getPriorityLabelColor = (priority: Notification["priority"]) => {
    switch (priority) {
      case "critical":
        return "bg-error-100 dark:bg-error-900/30 text-error-700 dark:text-error-300 border-error-300 dark:border-error-700";
      case "high":
        return "bg-warning-100 dark:bg-warning-900/30 text-warning-700 dark:text-warning-300 border-warning-300 dark:border-warning-700";
      default:
        return "";
    }
  };

  // Use centralized status colors
  const getStatusColor = getQuoteStatusColor;

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle keyboard shortcuts when not in an input field
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      const selectedNotification = selectedIndex !== null ? notifications[selectedIndex] : null;

      switch (e.key) {
        case "ArrowDown":
        case "j":
          e.preventDefault();
          setSelectedIndex((prev) => {
            if (prev === null) return 0;
            return Math.min(prev + 1, notifications.length - 1);
          });
          break;
        case "ArrowUp":
        case "k":
          e.preventDefault();
          setSelectedIndex((prev) => {
            if (prev === null) return notifications.length - 1;
            return Math.max(prev - 1, 0);
          });
          break;
        case "Enter":
          e.preventDefault();
          if (selectedNotification?.quoteId) {
            handleNotificationClick(selectedNotification);
          }
          break;
        case "r":
          e.preventDefault();
          if (selectedNotification?.quoteId) {
            handleQuickReply(selectedNotification);
          }
          break;
        case "m":
          e.preventDefault();
          if (selectedNotification && !selectedNotification.read) {
            markAsRead(selectedNotification._id);
          }
          break;
        case "a":
          e.preventDefault();
          if (selectedNotification) {
            archiveNotification(selectedNotification._id);
          }
          break;
        case "d":
        case "Delete":
          e.preventDefault();
          if (selectedNotification) {
            contextDeleteNotification(selectedNotification._id);
          }
          break;
        case "Escape":
          e.preventDefault();
          onClose();
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    notifications,
    selectedIndex,
    markAsRead,
    archiveNotification,
    contextDeleteNotification,
    onClose,
  ]);

  // Scroll selected notification into view
  useEffect(() => {
    if (selectedIndex !== null && notificationRefs.current[selectedIndex]) {
      notificationRefs.current[selectedIndex]?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [selectedIndex]);

  // Use unread count from context
  const unreadCount = contextUnreadCount;

  return (
    <>
      {/* Desktop Pane */}
      <div
        className={`hidden md:flex fixed ${isRTL ? "left-16" : "right-16"} top-0 bottom-0 w-[420px] bg-white dark:bg-gray-900 ${isRTL ? "border-r" : "border-l"} border-gray-200 dark:border-gray-800 shadow-xl flex-col z-30`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200 dark:border-gray-800 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-900">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-accent-100 dark:bg-accent-900/30 rounded-lg">
              <Bell className="w-5 h-5 text-accent-600 dark:text-accent-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Notifications
              </h3>
              {unreadCount > 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {unreadCount} unread
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-1">
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="p-2 text-gray-500 hover:text-accent-600 dark:text-gray-400 dark:hover:text-accent-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all"
                aria-label="Mark all as read"
                title="Mark all as read"
              >
                <CheckCheck className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all"
              aria-label="Close notifications"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Notifications List */}
        <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-full">
              <Loader2 className="w-10 h-10 text-accent-600 dark:text-accent-400 animate-spin mb-3" />
              <p className="text-sm text-gray-600 dark:text-gray-400">Loading notifications...</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full px-8 text-center">
              <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-full mb-4">
                <BellOff className="w-12 h-12 text-gray-400 dark:text-gray-600" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                All caught up!
              </h4>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                You have no notifications at the moment
              </p>
            </div>
          ) : (
            <div className="p-4 space-y-2">
              {notifications.map((notification, index) => {
                const isNotificationLoading =
                  loadingNotificationId === notification._id;
                const isQuickReplyLoading =
                  loadingQuickReplyId === notification._id;
                const isDisabled =
                  isNotificationLoading || isQuickReplyLoading || loadingQuote;
                const isSelected = selectedIndex !== null && index === selectedIndex;

                return (
                  <div
                    key={notification._id}
                    ref={(el) => {
                      notificationRefs.current[index] = el;
                    }}
                    tabIndex={0}
                    role="article"
                    aria-label={`Notification: ${notification.title}`}
                    className={`group relative p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl transition-all ${
                      !notification.read
                        ? `shadow-sm border-l-4 ${getPriorityBorderColor(notification.priority)}`
                        : "hover:border-gray-300 dark:hover:border-gray-600"
                    } ${notification.quoteId && !isDisabled ? "cursor-pointer hover:shadow-md" : ""} ${
                      isDisabled ? "opacity-60 cursor-not-allowed" : ""
                    } ${isSelected ? "ring-2 ring-accent-500 dark:ring-accent-400" : ""}`}
                    onClick={() =>
                      !isDisabled &&
                      notification.quoteId &&
                      handleNotificationClick(notification)
                    }
                  >
                    {/* Header */}
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className={`flex-shrink-0 mt-0.5 p-2 rounded-lg ${getIconBgColor(notification)}`}>
                          {getNotificationIcon(notification)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className={`font-semibold text-sm ${getNotificationCardHeading(notification.type || "info")}`}>
                              {notification.title}
                            </h4>
                            {!notification.read && (
                              <span className="flex-shrink-0 w-2 h-2 bg-accent-500 dark:bg-accent-400 rounded-full" />
                            )}
                          </div>
                          <p className={`text-sm leading-relaxed ${getNotificationCardText(notification.type || "info")}`}>
                            {notification.message}
                          </p>
                        </div>
                      </div>
                      
                      {/* Actions Menu */}
                      <div className="relative flex-shrink-0">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowActionsMenu(
                              showActionsMenu === notification._id
                                ? null
                                : notification._id
                            );
                          }}
                          disabled={isDisabled}
                          className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all opacity-0 group-hover:opacity-100 disabled:opacity-50"
                          aria-label="More actions"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>
                        
                        {showActionsMenu === notification._id && (
                          <div className="absolute right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg z-20 py-1 min-w-[180px]">
                            {!notification.read && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  markAsRead(notification._id);
                                  setShowActionsMenu(null);
                                }}
                                className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 flex items-center gap-3"
                              >
                                <Check className="w-4 h-4" />
                                Mark as read
                              </button>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowSnoozeMenu(
                                  showSnoozeMenu === notification._id
                                    ? null
                                    : notification._id
                                );
                              }}
                              className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 flex items-center gap-3"
                            >
                              <Clock className="w-4 h-4" />
                              Snooze
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                archiveNotification(notification._id);
                                setShowActionsMenu(null);
                              }}
                              className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 flex items-center gap-3"
                            >
                              <Archive className="w-4 h-4" />
                              Archive
                            </button>
                            <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                contextDeleteNotification(notification._id);
                                setShowActionsMenu(null);
                              }}
                              className="w-full text-left px-4 py-2.5 text-sm hover:bg-error-50 dark:hover:bg-error-900/20 text-error-600 dark:text-error-400 flex items-center gap-3"
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete
                            </button>
                          </div>
                        )}
                        
                        {/* Snooze submenu */}
                        {showSnoozeMenu === notification._id && (
                          <div className="absolute right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg z-30 py-1 min-w-[140px]">
                            {[
                              { label: "1 hour", duration: 60 },
                              { label: "3 hours", duration: 180 },
                              { label: "1 day", duration: 1440 },
                              { label: "3 days", duration: 4320 },
                            ].map((option) => (
                              <button
                                key={option.duration}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  snoozeNotification(
                                    notification._id,
                                    option.duration
                                  );
                                  setShowSnoozeMenu(null);
                                  setShowActionsMenu(null);
                                }}
                                className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                              >
                                {option.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between gap-3 mt-3">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDateTime(notification.createdAt)}
                        </span>
                        {notification.groupCount && notification.groupCount > 1 && (
                          <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                            {notification.groupCount}
                          </span>
                        )}
                        {getPriorityLabel(notification.priority) && (
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full font-medium border ${getPriorityLabelColor(notification.priority)}`}
                          >
                            {getPriorityLabel(notification.priority)}
                          </span>
                        )}
                      </div>
                      
                      {notification.quoteId && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            if (!isDisabled) {
                              handleQuickReply(notification);
                            }
                          }}
                          disabled={isDisabled}
                          className="text-xs px-3 py-1.5 bg-accent-600 dark:bg-accent-600 text-white rounded-lg hover:bg-accent-700 dark:hover:bg-accent-500 flex items-center gap-1.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-sm"
                          title="Reply"
                        >
                          {isQuickReplyLoading ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <MessageSquare className="w-3.5 h-3.5" />
                          )}
                          Reply
                        </button>
                      )}
                    </div>
                    
                    {isNotificationLoading && (
                      <div className="absolute inset-0 bg-white/50 dark:bg-gray-800/50 rounded-xl flex items-center justify-center">
                        <Loader2 className="w-6 h-6 text-accent-600 dark:text-accent-400 animate-spin" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Mobile Pane - Bottom Sheet Style */}
      <div className="md:hidden fixed inset-0 z-30 flex items-end">
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black bg-opacity-60 transition-opacity backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Bottom Sheet */}
        <div className="relative w-full bg-white dark:bg-gray-900 rounded-t-3xl shadow-2xl max-h-[85vh] flex flex-col animate-slide-up">
          {/* Drag Handle */}
          <div className="flex justify-center pt-3 pb-2">
            <div className="w-12 h-1.5 bg-gray-300 dark:bg-gray-700 rounded-full" />
          </div>

          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-800 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-900">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-accent-100 dark:bg-accent-900/30 rounded-lg">
                <Bell className="w-5 h-5 text-accent-600 dark:text-accent-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Notifications
                </h3>
                {unreadCount > 0 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {unreadCount} unread
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-1">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="p-2 text-gray-500 hover:text-accent-600 dark:text-gray-400 dark:hover:text-accent-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all"
                  aria-label="Mark all as read"
                  title="Mark all as read"
                >
                  <CheckCheck className="w-5 h-5" />
                </button>
              )}
              <button
                onClick={onClose}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all"
                aria-label="Close notifications"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center h-full">
                <Loader2 className="w-10 h-10 text-accent-600 dark:text-accent-400 animate-spin mb-3" />
                <p className="text-sm text-gray-600 dark:text-gray-400">Loading notifications...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full px-8 text-center">
                <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-full mb-4">
                  <BellOff className="w-12 h-12 text-gray-400 dark:text-gray-600" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  All caught up!
                </h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  You have no notifications at the moment
                </p>
              </div>
            ) : (
              <div className="p-4 space-y-2">
                {notifications.map((notification, index) => {
                  const isNotificationLoading =
                    loadingNotificationId === notification._id;
                  const isQuickReplyLoading =
                    loadingQuickReplyId === notification._id;
                  const isDisabled =
                    isNotificationLoading ||
                    isQuickReplyLoading ||
                    loadingQuote;
                  const isSelected = selectedIndex !== null && index === selectedIndex;

                  return (
                    <div
                      key={notification._id}
                      ref={(el) => {
                        notificationRefs.current[index] = el;
                      }}
                      tabIndex={0}
                      role="article"
                      aria-label={`Notification: ${notification.title}`}
                      className={`group relative p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl transition-all ${
                        !notification.read
                          ? `shadow-sm border-l-4 ${getPriorityBorderColor(notification.priority)}`
                          : "hover:border-gray-300 dark:hover:border-gray-600"
                      } ${notification.quoteId && !isDisabled ? "cursor-pointer active:scale-[0.98]" : ""} ${
                        isDisabled ? "opacity-60 cursor-not-allowed" : ""
                      } ${isSelected ? "ring-2 ring-accent-500 dark:ring-accent-400" : ""}`}
                      onClick={() =>
                        !isDisabled &&
                        notification.quoteId &&
                        handleNotificationClick(notification)
                      }
                    >
                      {/* Header */}
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <div className={`flex-shrink-0 mt-0.5 p-2 rounded-lg ${getIconBgColor(notification)}`}>
                            {getNotificationIcon(notification)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className={`font-semibold text-sm ${getNotificationCardHeading(notification.type || "info")}`}>
                                {notification.title}
                              </h4>
                              {!notification.read && (
                                <span className="flex-shrink-0 w-2 h-2 bg-accent-500 dark:bg-accent-400 rounded-full" />
                              )}
                            </div>
                            <p className={`text-sm leading-relaxed ${getNotificationCardText(notification.type || "info")}`}>
                              {notification.message}
                            </p>
                          </div>
                        </div>
                        
                        {/* Actions Menu */}
                        <div className="relative flex-shrink-0">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowActionsMenu(
                                showActionsMenu === notification._id
                                  ? null
                                  : notification._id
                              );
                            }}
                            disabled={isDisabled}
                            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all disabled:opacity-50"
                            aria-label="More actions"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                          
                          {showActionsMenu === notification._id && (
                            <div className="absolute right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg z-20 py-1 min-w-[180px]">
                              {!notification.read && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    markAsRead(notification._id);
                                    setShowActionsMenu(null);
                                  }}
                                  className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 flex items-center gap-3"
                                >
                                  <Check className="w-4 h-4" />
                                  Mark as read
                                </button>
                              )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowSnoozeMenu(
                                    showSnoozeMenu === notification._id
                                      ? null
                                      : notification._id
                                  );
                                }}
                                className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 flex items-center gap-3"
                              >
                                <Clock className="w-4 h-4" />
                                Snooze
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  archiveNotification(notification._id);
                                  setShowActionsMenu(null);
                                }}
                                className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 flex items-center gap-3"
                              >
                                <Archive className="w-4 h-4" />
                                Archive
                              </button>
                              <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  contextDeleteNotification(notification._id);
                                  setShowActionsMenu(null);
                                }}
                                className="w-full text-left px-4 py-2.5 text-sm hover:bg-error-50 dark:hover:bg-error-900/20 text-error-600 dark:text-error-400 flex items-center gap-3"
                              >
                                <Trash2 className="w-4 h-4" />
                                Delete
                              </button>
                            </div>
                          )}
                          
                          {/* Snooze submenu */}
                          {showSnoozeMenu === notification._id && (
                            <div className="absolute right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg z-30 py-1 min-w-[140px]">
                              {[
                                { label: "1 hour", duration: 60 },
                                { label: "3 hours", duration: 180 },
                                { label: "1 day", duration: 1440 },
                                { label: "3 days", duration: 4320 },
                              ].map((option) => (
                                <button
                                  key={option.duration}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    snoozeNotification(
                                      notification._id,
                                      option.duration
                                    );
                                    setShowSnoozeMenu(null);
                                    setShowActionsMenu(null);
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                                >
                                  {option.label}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Footer */}
                      <div className="flex items-center justify-between gap-3 mt-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDateTime(notification.createdAt)}
                          </span>
                          {notification.groupCount && notification.groupCount > 1 && (
                            <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                              {notification.groupCount}
                            </span>
                          )}
                          {getPriorityLabel(notification.priority) && (
                            <span
                              className={`text-xs px-2 py-0.5 rounded-full font-medium border ${getPriorityLabelColor(notification.priority)}`}
                            >
                              {getPriorityLabel(notification.priority)}
                            </span>
                          )}
                        </div>
                        
                        {notification.quoteId && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              if (!isDisabled) {
                                handleQuickReply(notification);
                              }
                            }}
                            disabled={isDisabled}
                            className="text-xs px-3 py-1.5 bg-accent-600 dark:bg-accent-600 text-white rounded-lg hover:bg-accent-700 dark:hover:bg-accent-500 flex items-center gap-1.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-sm"
                            title="Reply"
                          >
                            {isQuickReplyLoading ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <MessageSquare className="w-3.5 h-3.5" />
                            )}
                            Reply
                          </button>
                        )}
                      </div>
                      
                      {isNotificationLoading && (
                        <div className="absolute inset-0 bg-white/50 dark:bg-gray-800/50 rounded-xl flex items-center justify-center">
                          <Loader2 className="w-6 h-6 text-accent-600 dark:text-accent-400 animate-spin" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quote Detail Modal */}
      {showQuoteModal && selectedQuote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-6xl w-full my-8 max-h-[90vh] overflow-y-auto shadow-xl border border-gray-200 dark:border-gray-600">
            <div className="sticky top-0 bg-white dark:bg-gray-800 p-6 border-b border-gray-200 dark:border-gray-600 flex justify-between items-start z-10">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  View Quote
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {selectedQuote.quoteNumber} -{" "}
                  {selectedQuote.clientId?.name || "N/A"}
                </p>
                <span
                  className={`inline-block mt-2 px-3 py-1 text-xs rounded-full font-medium ${getStatusColor(selectedQuote.status)}`}
                >
                  {selectedQuote.status}
                </span>
              </div>
              <button
                onClick={() => {
                  setShowQuoteModal(false);
                  setSelectedQuote(null);
                }}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Title
                  </label>
                  <div className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                    {selectedQuote.title}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Valid Until
                  </label>
                  <div className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                    {selectedQuote.validUntil
                      ? formatDate(selectedQuote.validUntil)
                      : "N/A"}
                  </div>
                </div>
              </div>

              {selectedQuote.headerText && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Header Text
                  </label>
                  <div className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 whitespace-pre-wrap">
                    {selectedQuote.headerText}
                  </div>
                </div>
              )}

              {/* Line Items */}
              {selectedQuote.lineItems &&
                selectedQuote.lineItems.length > 0 && (
                  <div>
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        Line Items
                      </h3>
                    </div>

                    <div className="space-y-3">
                      {selectedQuote.lineItems.map((item, index) => (
                        <div
                          key={index}
                          className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700/50"
                        >
                          <div className="grid grid-cols-12 gap-3">
                            <div className="col-span-4">
                              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Description
                              </label>
                              <div className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                                {item.description}
                              </div>
                            </div>
                            <div className="col-span-2">
                              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Quantity
                              </label>
                              <div className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                                {item.quantity}
                              </div>
                            </div>
                            <div className="col-span-2">
                              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Unit Price
                              </label>
                              <div className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                                {selectedQuote.currencyCode}{" "}
                                {item.unitPrice.toFixed(2)}
                              </div>
                            </div>
                            <div className="col-span-2">
                              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Tax %
                              </label>
                              <div className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                                {item.taxRate.toFixed(2)}%
                              </div>
                            </div>
                            <div className="col-span-2">
                              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Total
                              </label>
                              <div className="px-2 py-1 text-sm bg-white dark:bg-gray-600 border border-gray-200 dark:border-gray-500 rounded font-semibold text-gray-900 dark:text-gray-100">
                                {selectedQuote.currencyCode}{" "}
                                {item.lineTotal.toFixed(2)}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Quote Totals */}
                    <div className="mt-4 flex justify-end">
                      <div className="w-64 space-y-2 bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">
                            Subtotal:
                          </span>
                          <span className="font-semibold text-gray-900 dark:text-gray-100">
                            {selectedQuote.currencyCode}{" "}
                            {selectedQuote.subtotal.toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">
                            Tax:
                          </span>
                          <span className="font-semibold text-gray-900 dark:text-gray-100">
                            {selectedQuote.currencyCode}{" "}
                            {selectedQuote.totalTax.toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between text-lg border-t border-gray-300 dark:border-gray-600 pt-2">
                          <span className="font-bold text-gray-900 dark:text-gray-100">
                            Total:
                          </span>
                          <span className="font-bold text-indigo-600 dark:text-indigo-400">
                            {selectedQuote.currencyCode}{" "}
                            {selectedQuote.total.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

              {selectedQuote.footerText && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Footer Text
                  </label>
                  <div className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 whitespace-pre-wrap">
                    {selectedQuote.footerText}
                  </div>
                </div>
              )}

              {selectedQuote.termsAndConditions && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Terms and Conditions
                  </label>
                  <div className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 whitespace-pre-wrap">
                    {selectedQuote.termsAndConditions}
                  </div>
                </div>
              )}

              {/* Additional Info */}
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Quote Number
                  </p>
                  <p className="text-base font-medium text-gray-900 dark:text-gray-100">
                    {selectedQuote.quoteNumber}
                  </p>
                </div>
                {selectedQuote.projectId && (
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Project
                    </p>
                    <p className="text-base font-medium text-gray-900 dark:text-gray-100">
                      {selectedQuote.projectId.name}
                    </p>
                  </div>
                )}
                {selectedQuote.issuedDate && (
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Issued Date
                    </p>
                    <p className="text-base font-medium text-gray-900 dark:text-gray-100">
                      {formatDate(selectedQuote.issuedDate)}
                    </p>
                  </div>
                )}
                {selectedQuote.sentDate && (
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Sent Date
                    </p>
                    <p className="text-base font-medium text-gray-900 dark:text-gray-100">
                      {formatDate(selectedQuote.sentDate)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Messages Modal */}
      {showMessagesModal && selectedQuote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-3xl w-full my-8 max-h-[90vh] flex flex-col shadow-xl border border-gray-200 dark:border-gray-600">
            <div className="p-6 border-b border-gray-200 dark:border-gray-600">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                    Quote Messages
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {selectedQuote?.quoteNumber || "Quote"} -{" "}
                    {selectedQuote?.title || "Loading..."}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowMessagesModal(false);
                    setSelectedQuote(null);
                    setQuoteMessages([]);
                    setReplyMessage("");
                    setSelectedQuoteId(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div
              className="flex-1 overflow-y-auto p-6"
              style={{
                willChange: "scroll-position",
                transform: "translateZ(0)",
              }}
            >
              {loadingMessages ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-600 dark:border-blue-400 mx-auto"></div>
                  <p className="text-gray-600 dark:text-gray-400 mt-4">
                    Loading messages...
                  </p>
                </div>
              ) : quoteMessages.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <MessageSquare className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">
                    No messages yet.
                  </p>
                </div>
              ) : (
                <div
                  className="space-y-4"
                  style={{ contain: "layout style paint" }}
                >
                  {quoteMessages.map((msg) => (
                    <div
                      key={msg._id}
                      className={`flex ${msg.isFromClient ? "justify-start" : "justify-end"}`}
                    >
                      <div
                        className={`max-w-lg px-4 py-3 rounded-lg ${
                          msg.isFromClient
                            ? "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            : "bg-accent-600 dark:bg-accent-700 text-white"
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-xs font-semibold">
                            {msg.isFromClient
                              ? msg.clientName
                              : msg.staffName || "You"}
                          </p>
                          {!msg.read && msg.isFromClient && (
                            <span className="text-xs bg-accent-500 text-white px-2 py-0.5 rounded-full">
                              New
                            </span>
                          )}
                        </div>
                        <p className="text-sm">{msg.message}</p>
                        <p
                          className={`text-xs mt-1 ${msg.isFromClient ? "text-gray-500 dark:text-gray-400" : "text-accent-200"}`}
                        >
                          {msg.formattedDate || formatDateTime(msg.createdAt)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700">
              <form onSubmit={handleSendReply} className="flex gap-2">
                <input
                  type="text"
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  placeholder="Type your reply..."
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
                  disabled={sendingReply}
                />
                <button
                  type="submit"
                  disabled={sendingReply || !replyMessage.trim()}
                  className="px-6 py-2 bg-accent-600 dark:bg-accent-700 text-white rounded-lg hover:bg-accent-700 dark:hover:bg-accent-600 disabled:bg-gray-400 dark:disabled:bg-gray-600 flex items-center gap-2"
                >
                  {sendingReply ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      Send
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
